import {
  MutableRefObject,
  useCallback,
  // useEffect, // RAGBOT
  useRef,
  useState
} from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil'; // RAGBOT: 'useRecoilValue' removed
import { v4 as uuidv4 } from 'uuid';

import {
  FileSpec,
  IStep,
  // commandsState, // RAGBOT: commands are disabled
  useAuth,
  useChatData,
  useChatInteract,
  // useConfig // RAGBOT
} from '@chainlit/react-client';
// import type { IMode, IModeOption } from '@chainlit/react-client'; // RAGBOT
// import { modesState } from '@chainlit/react-client'; // RAGBOT

// import { INPUT_MESSAGE_MAX_LENGTH_ID } from '@/components/ChatSettings'; // RAGBOT
import { Settings } from '@/components/icons/Settings';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { useTranslation } from 'components/i18n/Translator';

// import { useQuery } from '@/hooks/query'; // RAGBOT
import { useIsMobile } from '@/hooks/use-mobile';

import { chatSettingsOpenState } from '@/state/project';
import {
  IAttachment,
  attachmentsState,
  persistentCommandState
} from 'state/chat';

import { Attachments } from './Attachments';
import CommandButtons from './CommandButtons';
import CommandButton from './CommandPopoverButton';
// import FavoriteButton from './FavoriteButton'; // RAGBOT
import Input, { InputMethods } from './Input';
// import McpButton from './Mcp'; // RAGBOT
// import ModePicker from './ModePicker'; // RAGBOT
import SubmitButton from './SubmitButton';
import UploadButton from './UploadButton';
import VoiceButton from './VoiceButton';

interface Props {
  fileSpec: FileSpec;
  onFileUpload: (payload: File[]) => void;
  onFileUploadError: (error: string) => void;
  autoScrollRef: MutableRefObject<boolean>;
}

export default function MessageComposer({
  fileSpec,
  onFileUpload,
  onFileUploadError,
  autoScrollRef
}: Props) {
  const inputRef = useRef<InputMethods>(null);
  const [value, setValue] = useState('');
  const [selectedCommand, setSelectedCommand] = useRecoilState(
    persistentCommandState
  );
  // const commands = useRecoilValue(commandsState); // RAGBOT: disable commands
  const setChatSettingsOpen = useSetRecoilState(chatSettingsOpenState);
  /*
  // Pre-select the command marked as selected by the backend
  useEffect(() => {
    const defaultSelected = commands.find((c) => c.selected);
    if (defaultSelected && !selectedCommand) {
      setSelectedCommand(defaultSelected);
    }
  }, [commands]); */ // RAGBOT
  const [attachments, setAttachments] = useRecoilState(attachmentsState);
  const { t } = useTranslation();

  const { user } = useAuth();
  const { sendMessage, replyMessage } = useChatInteract();
  const { askUser, chatSettingsInputs, disabled: _disabled } = useChatData();

  const disabled = _disabled || !!attachments.find((a) => !a.uploaded);
  /* RAGBOT
  const { config } = useConfig();
  // RAGBOT: the hidden max-input-length widget must not make the settings
  // button appear when there is nothing else to configure.
  const visibleChatSettingsCount = chatSettingsInputs.filter(
    (input: any) => input?.id !== INPUT_MESSAGE_MAX_LENGTH_ID
  ).length;
  const showSettingsInComposer =
    config?.ui?.chat_settings_location !== 'sidebar' &&
    visibleChatSettingsCount > 0;
  */
  const isMobile = useIsMobile();
  /* // RAGBOT
  // Get/set available modes from state - selections are tracked via the 'default' flag on options
  const [modes, setModes] = useRecoilState(modesState);

  const handleModeSelect = useCallback(
    (modeId: string, optionId: string) => {
      setModes((prevModes) =>
        prevModes.map((mode) => {
          if (mode.id !== modeId) return mode;
          return {
            ...mode,
            options: mode.options.map((opt: IModeOption) => ({
              ...opt,
              default: opt.id === optionId
            }))
          };
        })
      );
    },
    [setModes]
  );

  // Helper to get selected option for a mode (the one with default=true, or first option)
  const getSelectedOptionId = useCallback((mode: IMode): string | undefined => {
    const defaultOpt = mode.options.find((opt) => opt.default);
    return defaultOpt?.id || mode.options[0]?.id;
  }, []);

  let promptValue = '';
  try {
    const query = useQuery();
    promptValue = query.get('prompt') || '';
  } catch {
    console.warn('Could not parse query parameters');
  }

  const [promptUsed, setPromptUsed] = useState(false);

  const onFavoriteSelect = useCallback((content: string) => {
    setValue(content);
    if (inputRef.current) {
      inputRef.current.setValueExtern(content);
    }
  }, []);
  */ // RAGBOT
  const onPaste = useCallback(
    (event: ClipboardEvent) => {
      if (event.clipboardData && event.clipboardData.items) {
        const items = Array.from(event.clipboardData.items);

        // If no text data, check for files (e.g., images)
        items.forEach((item) => {
          if (item.kind === 'file') {
            const file = item.getAsFile();
            if (file) {
              onFileUpload([file]);
            }
          }
        });
      }
    },
    [onFileUpload]
  );

  const onSubmit = useCallback(
    async (
      msg: string,
      attachments?: IAttachment[],
      selectedCommand?: string
    ) => { /* // RAGBOT
      // Build modes dict: only include modes that have selections
      const modesDict: Record<string, string> = {};
      modes.forEach((mode) => {
        const selectedId = getSelectedOptionId(mode);
        if (selectedId) {
          modesDict[mode.id] = selectedId;
        }
      }); */

      const message: IStep = {
        threadId: '',
        command: selectedCommand,
        // modes: Object.keys(modesDict).length > 0 ? modesDict : undefined, // RAGBOT
        id: uuidv4(),
        name: user?.identifier || 'User',
        type: 'user_message',
        output: msg,
        createdAt: new Date().toISOString(),
        metadata: { location: window.location.href }
      };

      const fileReferences = attachments
        ?.filter((a) => !!a.serverId)
        .map((a) => ({ id: a.serverId! }));

      if (autoScrollRef) {
        autoScrollRef.current = true;
      }
      sendMessage(message, fileReferences);
    },
    [user, sendMessage, autoScrollRef] // RAGBOT: 'modes' and 'getSelectedOptionId' are removed from the array
  );

  const onReply = useCallback(
    async (msg: string) => {
      const message: IStep = {
        threadId: '',
        id: uuidv4(),
        name: user?.identifier || 'User',
        type: 'user_message',
        output: msg,
        createdAt: new Date().toISOString(),
        metadata: { location: window.location.href }
      };

      replyMessage(message);
      if (autoScrollRef) {
        autoScrollRef.current = true;
      }
    },
    [user, replyMessage, autoScrollRef]
  );

  const submit = useCallback(() => {
    if (
      disabled ||
      (value.trim() === '' && attachments.length === 0 && !selectedCommand)
    ) {
      return;
    }

    if (askUser) {
      onReply(value);
    } else {
      onSubmit(value, attachments, selectedCommand?.id);
    }

    setAttachments([]);
    setValue(''); // Clear the value state
    inputRef.current?.reset();
  }, [
    value,
    disabled,
    askUser,
    attachments,
    selectedCommand,
    setAttachments,
    onSubmit,
    onReply
  ]);
  /* RAGBOT
  useEffect(() => {
    if (inputRef.current && promptValue && !promptUsed) {
      const prompt = promptValue;
      if (prompt) {
        if (prompt.length > 1000) {
          inputRef.current?.setValueExtern(prompt.slice(0, 1000));
        } else {
          inputRef.current?.setValueExtern(prompt);
        }
        setPromptUsed(true);
      }
    }
  }, [promptValue, promptUsed]); */ // RAGBOT

  return (
    <div
      id="message-composer"
      className="bg-accent dark:bg-card rounded-3xl p-3 px-4 w-full min-h-24 flex flex-col"
    >
      {attachments.length > 0 ? (
        <div className="mb-1">
          <Attachments />
        </div>
      ) : null}
      <Input
        ref={inputRef}
        id="chat-input"
        autoFocus={!isMobile}
        selectedCommand={selectedCommand}
        setSelectedCommand={setSelectedCommand}
        onChange={setValue}
        onPaste={onPaste}
        onEnter={submit}
        placeholder={t('chat.input.placeholder')}
      />
      <div className="flex items-center justify-between">
        {/* RAGBOT: the voice button sits after the MCP button in this layout. */}
        <div className="flex items-center -ml-1.5">
          <UploadButton
            disabled={disabled}
            fileSpec={fileSpec}
            onFileUploadError={onFileUploadError}
            onFileUpload={onFileUpload}
          />
          {/* {showSettingsInComposer && ( */}{/* RAGBOT */}
          {chatSettingsInputs.length > 1 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    id="chat-settings-open-modal"
                    disabled={disabled}
                    onClick={() => setChatSettingsOpen(true)}
                    className="hover:bg-muted rounded-full"
                    variant="ghost"
                    size="icon"
                  >
                    <Settings className="!size-6" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('navigation.user.menu.settings')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {/* <McpButton disabled={disabled} /> */}{/* RAGBOT */}
          <VoiceButton disabled={disabled} />
          {/*modes.map((mode) => ( //RAGBOT
            <ModePicker
              key={mode.id}
              mode={mode}
              disabled={disabled}
              selectedOptionId={getSelectedOptionId(mode)}
              onOptionSelect={handleModeSelect}
            />
          ))*/}
          <CommandButton
            disabled={disabled}
            selectedCommandId={selectedCommand?.id}
            onCommandSelect={setSelectedCommand}
          />
          <CommandButtons
            disabled={disabled}
            selectedCommandId={selectedCommand?.id}
            onCommandSelect={setSelectedCommand}
          />

          {/* <FavoriteButton disabled={disabled} onSelect={onFavoriteSelect} /> */}{/* RAGBOT */}
        </div>
        <div className="flex items-center gap-1">
          <SubmitButton
            onSubmit={submit}
            disabled={
              disabled ||
              (!value.trim() && !selectedCommand && attachments.length === 0)
            }
          />
        </div>
      </div>
    </div>
  );
}
